import { isUndefined } from 'ramda-adjunct'
import {
  onBeforeMount,
  readonly,
  ref,
  type Ref,
  shallowReadonly,
  shallowRef,
  unref,
  watch,
  watchEffect,
} from 'vue'

import { getYoutubeIframeApiSingleton } from '../getYoutubeIframeApi'
import type { ReadonlyRefOrRaw } from '../types/RefOrRaw'
import { animationFrameInterval } from '../utils/animationFrameInterval'

export const YT_CONSTANTS = {
  PLAYER_STATE: {
    UNSTARTED: -1,
    ENDED: 0,
    PLAYING: 1,
    PAUSED: 2,
    BUFFERING: 3,
    CUED: 5,
  },
} as const

const useYoutubeIframeApi = (): Readonly<Ref<typeof globalThis.YT | undefined>> => {
  const YoutubeIframeApi = ref<typeof globalThis.YT | undefined>()

  onBeforeMount(async () => {
    YoutubeIframeApi.value = await getYoutubeIframeApiSingleton()
  })

  return readonly(YoutubeIframeApi)
}

/*
 * This safe list intentionally omits `videoId` and `events`.
 * This is to prevent the YT.Player from reinstantiating.
 * `videoId` should be passed in as the second argument
 * `events` should be added with `useYoutubePlayerEvent`
 *
 * Other keys are omitted just because they are unused
 */
const PLAYER_OPTIONS_SAFE_LIST = ['playerVars'] as const
export const useYoutubePlayer = (
  elRefOrRaw: ReadonlyRefOrRaw<HTMLDivElement | undefined>,
  videoIdRefOrRaw: ReadonlyRefOrRaw<string>,
  ytPlayerOptionsRefOrRaw?: ReadonlyRefOrRaw<
    Pick<YT.PlayerOptions, (typeof PLAYER_OPTIONS_SAFE_LIST)[number]>
  >,
): Readonly<Ref<YT.Player | undefined>> => {
  const youtubePlayer = shallowRef<YT.Player | undefined>()

  const YoutubeIframeApi = useYoutubeIframeApi()

  watchEffect(async (onCleanup) => {
    if (isUndefined(YoutubeIframeApi.value)) {
      return
    }
    const el = unref(elRefOrRaw)
    if (isUndefined(el)) {
      return
    }
    const player = new YoutubeIframeApi.value.Player(el, {
      height: 'auto',
      width: 'auto',
      ...unref(ytPlayerOptionsRefOrRaw)?.playerVars,
    })
    player.addEventListener('onReady', () => {
      youtubePlayer.value = player
    })
    onCleanup(() => {
      player.destroy()
      youtubePlayer.value = undefined
    })
  })

  watchEffect(() => {
    if (isUndefined(youtubePlayer.value)) {
      return
    }
    youtubePlayer.value.cueVideoById(unref(videoIdRefOrRaw))
  })

  return shallowReadonly(youtubePlayer)
}

// Parameterised on the event *name*, not the event type. The two are not
// independent -- `Player.addEventListener` demands a listener matching the name it
// is given -- so a free `TEvent` let a caller pair `'onError'` with a handler for
// any other event, and no longer typechecks against @types/youtube's signature.
export const useYoutubePlayerEvent = <TEventName extends keyof YT.Events>(
  playerRefOrRaw: ReadonlyRefOrRaw<YT.Player | undefined>,
  eventNameRefOrRaw: ReadonlyRefOrRaw<TEventName>,
  handlerRefOrRaw: ReadonlyRefOrRaw<NonNullable<YT.Events[TEventName]>>,
): (() => void) => {
  return watchEffect((onCleanup) => {
    const player = unref(playerRefOrRaw)
    if (isUndefined(player)) {
      return
    }

    const [eventName, handler] = [unref(eventNameRefOrRaw), unref(handlerRefOrRaw)]
    player.addEventListener(eventName, handler)

    onCleanup(() => {
      // player might have been destroyed, hence unref again
      unref(playerRefOrRaw)?.removeEventListener(eventName, handler)
    })
  })
}

export const useYoutubePlayerState = (
  playerRefOrRaw: ReadonlyRefOrRaw<YT.Player | undefined>,
): Readonly<Ref<YT.PlayerState>> => {
  const playerState: Ref<YT.PlayerState> = ref(YT_CONSTANTS.PLAYER_STATE.UNSTARTED)

  watch(
    () => unref(playerRefOrRaw),
    (player) => {
      if (isUndefined(player)) {
        playerState.value = YT_CONSTANTS.PLAYER_STATE.UNSTARTED
        return
      }
      playerState.value = player.getPlayerState()
    },
    { immediate: true },
  )

  useYoutubePlayerEvent(playerRefOrRaw, 'onStateChange', () => {
    const player = unref(playerRefOrRaw)
    if (!player) {
      return
    }
    playerState.value = player.getPlayerState()
  })

  return readonly(playerState)
}

export const useYoutubePlayerProgress = (
  playerRefOrRaw: ReadonlyRefOrRaw<YT.Player | undefined>,
): Readonly<Ref<number>> => {
  const progress = ref(0)
  const state = useYoutubePlayerState(playerRefOrRaw)

  const getProgress = (player: YT.Player | undefined): number => {
    if (isUndefined(player)) {
      return 0
    }
    const duration = player.getDuration()
    if (duration === 0) {
      return 0
    }
    return player.getCurrentTime() / duration
  }

  watch(
    () => unref(playerRefOrRaw),
    (player) => {
      progress.value = getProgress(player)
    },
  )

  watch(
    () => [unref(playerRefOrRaw), state.value] as const,
    ([player, state], _, onCleanup) => {
      if (state === YT_CONSTANTS.PLAYER_STATE.PAUSED) {
        return
      }
      if (state === YT_CONSTANTS.PLAYER_STATE.ENDED) {
        progress.value = 1
        return
      }

      const stopInterval = animationFrameInterval(() => {
        progress.value = getProgress(player)
      })

      onCleanup(stopInterval)
    },
  )

  return readonly(progress)
}
